// src/components/Features.jsx
import React from "react";
import { Card, CardContent } from "./ui/card";
import {
  Shield,
  Activity,
  VolumeX,
  Wind,
  Zap,
  Smartphone,
} from "lucide-react";
import { features } from "../mock";

const iconMap = {
  shield: Shield,
  activity: Activity,
  "volume-x": VolumeX,
  wind: Wind,
  zap: Zap,
  smartphone: Smartphone,
};

export const Features = () => {
  return (
    <section
      id="features"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-white relative"
    >
      <div className="max-w-7xl mx-auto">

        {/* --------------------------- HEADER --------------------------- */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-[var(--aerzen-black)] mb-4 animate-fadeIn">
            Powerful Features.  
            <span className="text-[var(--aerzen-blue)]">Built for India.</span>
          </h2>

          <p className="text-lg text-[var(--aerzen-black)]/70 max-w-2xl mx-auto animate-fadeIn delay-100 leading-relaxed">
            AERZEN AIR™ air purifiers combine medical-grade filtration with
            intelligent technology — ensuring your family breathes safer, cleaner air every day.
          </p>
        </div>

        {/* ------------------------- FEATURE GRID ------------------------- */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fadeIn delay-200">
          {features.map((feature) => {
            const Icon = iconMap[feature.icon];

            return (
              <Card
                key={feature.id}
                className="border border-[var(--aerzen-silver)] hover:border-[var(--aerzen-blue)] hover:shadow-xl hover:scale-[1.02] transition-all duration-300 group bg-white rounded-2xl"
              >
                <CardContent className="p-8">
                  
                  {/* Icon */}
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--aerzen-blue)]/10 group-hover:bg-[var(--aerzen-blue)] transition-all duration-300 shadow-sm">
                      <Icon className="h-8 w-8 text-[var(--aerzen-blue)] group-hover:text-white transition-all duration-300" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-[var(--aerzen-black)] mb-3">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[var(--aerzen-black)]/70 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* --------------------- WHY CHOOSE AERZEN --------------------- */}
        <div className="mt-24 bg-[var(--aerzen-blue)]/5 rounded-3xl p-8 sm:p-12 animate-fadeIn delay-300 shadow-inner">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-3xl sm:text-4xl font-bold text-[var(--aerzen-black)] text-center mb-12">
              Why People Trust AERZEN AIR™
            </h3>

            <div className="grid md:grid-cols-2 gap-10">

              {/* LEFT SIDE */}
              <div className="space-y-8">
                {[
                  {
                    num: "1",
                    title: "Premium Performance, Honest Pricing",
                    desc:
                      "Top-tier HEPA filtration and advanced sensors — without the inflated luxury pricing of imported brands.",
                  },
                  {
                    num: "2",
                    title: "Designed for Indian Air Conditions",
                    desc:
                      "Engineered to tackle PM2.5, dust, smoke, allergens and seasonal spikes like Diwali pollution.",
                  },
                ].map((item) => (
                  <div key={item.num} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-[var(--aerzen-blue)] flex items-center justify-center mt-1">
                      <span className="text-white font-bold text-sm">
                        {item.num}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--aerzen-black)] mb-2">
                        {item.title}
                      </h4>
                      <p className="text-[var(--aerzen-black)]/70">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* RIGHT SIDE */}
              <div className="space-y-8">
                {[
                  {
                    num: "3",
                    title: "2-Year Comprehensive Warranty",
                    desc:
                      "AERZEN AIR™ provides complete peace of mind with reliable support and easy service availability.",
                  },
                  {
                    num: "4",
                    title: "International Safety Certified",
                    desc:
                      "Each purifier undergoes rigorous testing for performance, safety and long-term durability.",
                  },
                ].map((item) => (
                  <div key={item.num} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full bg-[var(--aerzen-blue)] flex items-center justify-center mt-1">
                      <span className="text-white font-bold text-sm">
                        {item.num}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-bold text-[var(--aerzen-black)] mb-2">
                        {item.title}
                      </h4>
                      <p className="text-[var(--aerzen-black)]/70">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
